import { ConnectPatientPrompts } from './ui/connect-patient-prompts';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, map, shareReplay, timeout } from 'rxjs';
import { GroupService } from '../core/services/group.service';
import { PatientService } from '../core/services/patient.service';
import { ChatStore } from './core/chat-store';
import { HubConnection, HubStatus, HubTokenGrant } from './core/hub-connection';
import { ChatUiService } from './chat-ui.service';
import { BillingRelation, ConversationSummary, HubOrg, HubRequestError, MessageView, PatientChatActivity, PatientMention } from './core/hub-types';
import { buildPatientIndex, MentionPatient, normalise, PatientCandidate, PatientIndex, patientTitle } from './core/patient-mentions';
import { stateUnitGroupIds, visibleInStateMode } from './ui/state-mode-chats';

/**
 * A patient linked in a network chat, as its SENDER sees it under the message (network chat, 2026-09-22): being checked,
 * not referred to the other side yet (ask to refer), or referred (attributed, with when). Only this browser, this page.
 */
export interface NetworkReferralPrompt {
  patientRef: string;
  title: string;
  /**
   * checking/ask/error as before; 'linking' while the message is being linked into the referral chat (both sides then
   * see it, and the prompt goes); 'referred' only when that could not be done (the clinician has not joined yet);
   * 'declined' after "Not now" (the chip then says it is not shared, with a way to refer later).
   */
  state: 'checking' | 'ask' | 'linking' | 'referred' | 'error' | 'declined';
  since: string | null;
}

/** What the composer needs to find patients in a message (§8): all in memory, nothing leaves the browser. */
export interface MentionContext {
  index: PatientIndex;
  recent: ReadonlySet<string>;
  colleagueSurnames: ReadonlySet<string>;
}

/**
 * Angular glue for chat (tasks/messaging-hub-architecture.md M1): owns the one HubConnection and ChatStore of this
 * document, and ties them to the app — the signed-in user's token endpoint, the selected practice, page visibility.
 *
 *   enabled$      null until known; false when this platform has chat switched off (POST api/hub/token -> 404), in
 *                 which case nothing chat-related is shown and no socket is ever opened
 *   orgId$        the hub org of the practice selected in the app (GroupService); chat always shows ONE practice
 *   unread$       unread messages in that practice, for the header badge
 *
 * Browser only: server-side rendering never opens a socket.
 */
@Injectable({ providedIn: 'root' })
export class ChatService implements OnDestroy {
  readonly store = new ChatStore();
  /**
   * Network chat referral prompts by message id (2026-09-22). Kept here, not in the thread, so they survive the window
   * closing or switching conversation; not stored anywhere, so a reload forgets them (the referral itself is kept).
   */
  readonly networkPrompts = new Map<string, NetworkReferralPrompt[]>();
  /** MedicAI Connect chat prompts ("Connect doesn't have this patient, send them?"), per practice, kept like those above. */
  private readonly connectPrompts = new Map<string, ConnectPatientPrompts>();
  private connection: HubConnection | null = null;
  private readonly enabled = new BehaviorSubject<boolean | null>(null);
  readonly enabled$ = this.enabled.asObservable();
  private readonly status = new BehaviorSubject<HubStatus>('idle');
  readonly status$ = this.status.asObservable();
  readonly orgId$: Observable<string | null>;
  readonly unread$: Observable<number>;
  private readonly isBrowser: boolean;
  private visibilityHandler: (() => void) | null = null;
  private rosterSyncedAt = 0;
  private rosterSync: Promise<void> | null = null;
  /** The practice's patients (PatientService.patients$, every page already loaded), indexed for smart mentions. */
  private patientIndex: PatientIndex = buildPatientIndex([]);
  private recentPatients: ReadonlySet<string> = new Set();
  private colleagues = new Map<string, { at: number; surnames: ReadonlySet<string> }>();

  constructor(
    private readonly http: HttpClient,
    private readonly groupService: GroupService,
    private readonly zone: NgZone,
    private readonly ui: ChatUiService,
    private readonly patientService: PatientService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.groupService.userGroups$.subscribe((groups) => (this.unitGroupIds = stateUnitGroupIds(groups)));
    // Rebuilt when the list changes (a practice switch, a page arriving, a new patient); a burst is one rebuild.
    this.patientService.patients$.pipe(debounceTime(300)).subscribe((list: MentionPatient[]) => (this.patientIndex = buildPatientIndex(list)));
    this.patientService.recentPatients$.subscribe((list: { id?: unknown }[]) => (this.recentPatients = new Set((list ?? []).map((p) => String(p?.id)))));
    this.orgId$ = combineLatest([this.groupService.selectedGroup$, this.store.state$.pipe(map((s) => s.me), distinctUntilChanged())]).pipe(
      map(([group, me]) => (group && me ? me.orgs.find((o) => o.ref === String(group.id))?.orgId ?? null : null)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.unread$ = this.store.unreadOf$(this.orgId$).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: false }));
  }

  /** Starts chat for the signed-in user (idempotent). Safe to call from any component shown after login. */
  async ensureStarted(): Promise<void> {
    if (!this.isBrowser || this.connection || this.enabled.value === false) return;
    const connection = new HubConnection(() => this.fetchToken());
    this.connection = connection;
    // Socket events arrive outside Angular's zone; bring state changes back in so views update.
    connection.onStatus((s) => this.zone.run(() => this.status.next(s)));
    this.store.attach({
      request: (event, payload) => connection.request(event, payload),
      onEvent: (l) => connection.onEvent((e) => this.zone.run(() => l(e))),
      onReady: (l) => connection.onReady((r) => this.zone.run(() => l(r))),
      // Files go straight to the hub over HTTP (uploads, and the short-lived URLs that show them).
      baseUrl: () => connection.baseUrl(),
    });
    // Files in a referral chat with a clinician outside MedicAI: each message needs leave from this platform first.
    this.store.setFileGrantProvider((conversationId, clientMessageId) => this.referralFileGrant(conversationId, clientMessageId));
    // Once connected, bring every colleague into the hub (the people pickers list the whole practice).
    connection.onReady(() => void this.syncRoster().catch(() => undefined));
    // MedicAI Support is always in the list (owner, 2026-09-21), not only after a first message: provisioned per practice.
    connection.onReady((ready) => void this.provisionSupport(ready.orgs));
    this.watchVisibility();
    try {
      await connection.start();
      this.enabled.next(true);
    } catch (e) {
      if (e instanceof HubRequestError && e.code === 'DISABLED') {
        this.enabled.next(false);
        this.stop();
      }
      // Anything else (network, 503): stay "unknown" and let a later ensureStarted() try again.
      else this.connection = null;
    }
  }

  stop(): void {
    this.connection?.stop();
    this.connection = null;
    // A shared computer must not show one person's chat to the next.
    this.store.forgetOnThisDevice();
    this.networkPrompts.clear();
    this.connectPrompts.clear();
    this.supportProvisioned.clear(); // the next person to sign in on this page gets theirs
    this.store.dispose();
    if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.visibilityHandler = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private async fetchToken(): Promise<HubTokenGrant> {
    const token = localStorage.getItem('token');
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: HubTokenGrant }>('api/hub/token', {}, { headers: { Authorization: `Bearer ${token}` } }),
      );
      return res.data;
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 404) throw new HubRequestError('DISABLED');
      throw new HubRequestError('TOKEN_UNAVAILABLE');
    }
  }

  /**
   * Asks this app's API for a signed roster of the user's practices and forwards it to the hub, at most every five
   * minutes (or at once with `force`). The hub accepts it for the user's own practices only.
   */
  syncRoster(force = false): Promise<void> {
    if (this.rosterSync) return this.rosterSync;
    if (!force && Date.now() - this.rosterSyncedAt < 5 * 60_000) return Promise.resolve();
    this.rosterSync = (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await firstValueFrom(
          this.http.post<{ success: boolean; data: { token: string | null } }>('api/hub/roster', {}, { headers: { Authorization: `Bearer ${token}` } }),
        );
        if (res.data?.token) await this.store.syncRoster(res.data.token);
        this.rosterSyncedAt = Date.now();
      } finally {
        this.rosterSync = null;
      }
    })();
    return this.rosterSync;
  }

  private watchVisibility(): void {
    if (!this.isBrowser || this.visibilityHandler) return;
    this.visibilityHandler = () => this.zone.run(() => this.store.setVisible(document.visibilityState === 'visible'));
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.store.setVisible(document.visibilityState === 'visible');
  }

  /** Starts chat if needed and waits until the hub knows who we are. Rejects when chat is off or unreachable. */
  private async whenReady(): Promise<void> {
    await this.ensureStarted();
    if (this.enabled.value === false) throw new HubRequestError('DISABLED');
    await firstValueFrom(this.store.state$.pipe(filter((s) => !!s.me), timeout(20_000)));
  }

  private orgOfPatient(patient: { groupId?: string | null }): string {
    const org = this.store.orgByRef(patient.groupId ? String(patient.groupId) : null);
    if (!org) throw new HubRequestError('NOT_FOUND');
    return org.orgId;
  }

  /** Opens the floating window on the practice's chat about `patient` (tasks/messaging-hub-architecture.md §7, M2). */
  async openPatientChat(patient: { id: string; groupId?: string | null; title?: string | null; name?: string | null; surname?: string | null }): Promise<void> {
    await this.whenReady();
    const c = await this.store.openPatient(this.orgOfPatient(patient), String(patient.id), patientTitle(patient));
    this.ui.openConversation(c.id);
  }

  // ── a money card's Chat button (the Connect page) ───────────────────────────────

  /** The practice's live billing relations: its billing teams on MedicAI Connect. Empty when none or chat is off. */
  async billingTeams(groupId: string): Promise<BillingRelation[]> {
    try {
      await this.whenReady();
      const org = this.store.orgByRef(String(groupId));
      if (!org) return [];
      return (await this.store.billingRelations()).filter((r) => r.orgId === org.orgId && !r.ended);
    } catch {
      return [];
    }
  }

  /** The billing chats this user is in where the patient was tagged, latest first. */
  async patientBillingChats(patient: { id: string; groupId: string; title: string }): Promise<ConversationSummary[]> {
    await this.whenReady();
    return this.store.billingPatientChats(this.orgOfPatient(patient), { patientRef: String(patient.id), title: patient.title, system: 'clinical' });
  }

  /** Opens a chat with the patient waiting to be tagged in its message box. */
  async openWithPatient(conversationId: string, patient: { id: string; title: string }): Promise<void> {
    this.store.prefillPatient(String(conversationId).toLowerCase(), { patientRef: String(patient.id), title: patient.title });
    await this.openConversationById(conversationId);
  }

  /**
   * Opens the window on the conversation list searched for the patient's name: their own chat, referral chats about
   * them and their patient-app chat, to pick who to write to. Their own chat is made first if nobody has opened it
   * yet, so the search always finds it.
   */
  async openPatientSearch(patient: { id: string; groupId?: string | null; name?: string | null; surname?: string | null }): Promise<void> {
    await this.whenReady();
    const title = patientTitle(patient);
    await this.store.openPatient(this.orgOfPatient(patient), String(patient.id), title);
    this.ui.openSearch(title);
  }

  /**
   * Opens the floating window on a referral chat (M6): this app's server signs a one-use ticket for the caller's side of
   * the referral (`recipientId`, as the practice `groupId`, by default the selected one), and the hub files the chat
   * under it. Rejects NOT_ACCEPTED when this practice has not accepted the referral yet.
   */
  async openReferralChat(recipientId: string, groupId?: string | null): Promise<void> {
    await this.whenReady();
    const c = await this.store.openReferral(await this.referralTicket(recipientId, groupId));
    this.ui.openConversation(c.id);
  }

  /**
   * Makes sure the referral chat for `recipientId` EXISTS, without opening it — called when a referral is sent.
   *
   * A referral chat used to come into being only when somebody wrote in it, and the only people who could were the
   * external clinician on their dashboard, or a practice member who went into the patient file, found the recipient
   * and pressed Chat. So in practice every conversation started on the dashboard. Sending a referral now opens the
   * room, empty, and it is simply there in the sender's list to be written in.
   *
   * BEST EFFORT. Chat may be off or unreachable, and a referral must never fail because of it; the chat is still
   * created on demand the first time anyone opens it, exactly as before.
   */
  async ensureReferralChat(recipientId: string, groupId?: string | null): Promise<void> {
    await this.whenReady();
    await this.store.openReferral(await this.referralTicket(recipientId, groupId));
  }

  /**
   * A network chat's message, linked into the REFERRAL chat about a patient this practice referred over the chat's link
   * (2026-09-22): the other side then sees the link too. The ticket is the one that opens that referral chat, so the
   * hub accepts it only for the chat's own link and a referral still shared.
   */
  async linkReferral(messageId: string, recipientId: string, groupId: string): Promise<void> {
    await this.whenReady();
    await this.store.linkReferral(messageId, await this.referralTicket(recipientId, groupId));
  }

  /**
   * Opens (or finds) the caller's 1-on-1 with someone in the practice's referral network (2026-09-22) and shows it in
   * the floating window: a linked practice's doctor (`userId`), or the external clinician on `linkId` (no `userId`).
   * This app's server checks the link and the person, and signs the one-use ticket the hub opens the room with.
   */
  async openNetworkChat(groupId: string, linkId: string, userId?: string | null): Promise<ConversationSummary> {
    await this.whenReady();
    const token = localStorage.getItem('token');
    let ticket: string;
    try {
      const res = await firstValueFrom(this.http.post<{ success: boolean; data: { ticket: string } }>(
        'api/hub/network-chat', { groupId, linkId, ...(userId ? { userId } : {}) }, { headers: { Authorization: `Bearer ${token}` } },
      ));
      ticket = res.data.ticket;
    } catch (e) {
      throw new HubRequestError(e instanceof HttpErrorResponse && e.status === 404 ? 'NOT_FOUND' : 'TOKEN_UNAVAILABLE');
    }
    const c = await this.store.openNetwork(ticket);
    this.ui.openConversation(c.id);
    return c;
  }

  /** The "send this patient to MedicAI Connect" prompts of one practice (a clinical group). */
  connectPromptsOf(groupId: string): ConnectPatientPrompts {
    let prompts = this.connectPrompts.get(groupId);
    if (!prompts) {
      const url = (patientRef: string) => `api/coding-submit/group/${encodeURIComponent(groupId)}/patients/${encodeURIComponent(patientRef)}/connect`;
      const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
      prompts = new ConnectPatientPrompts({
        status: async (patientRef) => (await firstValueFrom(
          this.http.get<{ success: boolean; data: { connectLinked: boolean; onConnect: boolean } }>(url(patientRef), { headers: headers() }),
        )).data,
        send: (patientRef) => firstValueFrom(this.http.post(url(patientRef), {}, { headers: headers() })),
      });
      this.connectPrompts.set(groupId, prompts);
    }
    return prompts;
  }

  /** The practice's own id (the clinical group) for a hub org, or null when this session does not know it. */
  groupIdOfOrg(orgId: string | null | undefined): string | null {
    return this.store.snapshot.me?.orgs.find((o) => o.orgId === orgId)?.ref ?? null;
  }

  /** A state unit's practice (tasks/state-mode.md): no patient app, referral, colleague or Connect billing chats. */
  private unitGroupIds = new Set<string>();

  isStateUnitOrg(orgId: string | null | undefined): boolean {
    const groupId = this.groupIdOfOrg(orgId);
    return !!groupId && this.unitGroupIds.has(groupId);
  }

  /**
   * Leave to send one message with files in a referral chat with a clinician outside MedicAI (document allowance, owner
   * 2026-09-29): this platform spends one of the referral's documents on it. The chat is named by its key.
   */
  private async referralFileGrant(conversationId: string, clientMessageId: string): Promise<string> {
    const c = this.store.snapshot.conversations[conversationId];
    const groupId = this.groupIdOfOrg(c?.orgId);
    if (!c?.referral || !groupId) throw new HubRequestError('FILE_GRANT_REQUIRED');
    const token = localStorage.getItem('token');
    try {
      const res = await firstValueFrom(this.http.post<{ success: boolean; data: { grant: string } }>(
        'api/hub/referral-chat/file-grant',
        { groupId, linkRef: c.referral.linkRef, ownerPatientRef: c.referral.ownerPatientRef, clientMessageId },
        { headers: { Authorization: `Bearer ${token}` } },
      ));
      return res.data.grant;
    } catch (e) {
      throw new HubRequestError(e instanceof HttpErrorResponse && e.status === 409 ? 'DOCUMENT_ALLOWANCE_USED' : 'FILE_GRANT_REQUIRED');
    }
  }

  /** This app's one-use ticket for the caller's side of a referral, which the hub files the conversation under. */
  private async referralTicket(recipientId: string, groupId?: string | null): Promise<string> {
    const gid = groupId ?? (await firstValueFrom(this.groupService.selectedGroup$))?.id ?? null;
    const token = localStorage.getItem('token');
    try {
      const res = await firstValueFrom(this.http.post<{ success: boolean; data: { ticket: string } }>(
        'api/hub/referral-chat', { recipientId, groupId: gid }, { headers: { Authorization: `Bearer ${token}` } },
      ));
      return res.data.ticket;
    } catch (e) {
      if (e instanceof HttpErrorResponse && e.status === 409) throw new HubRequestError('NOT_ACCEPTED');
      throw new HubRequestError(e instanceof HttpErrorResponse && e.status === 404 ? 'NOT_FOUND' : 'TOKEN_UNAVAILABLE');
    }
  }

  /**
   * A short-lived URL for one chat file (the hub keeps chat files; the patient record keeps their names and opens them
   * from here). Starts chat if needed. Rejects when the caller may not read the message now.
   */
  async fileUrl(attachmentId: string): Promise<string> {
    await this.whenReady();
    return this.store.fileUrl(attachmentId);
  }

  /** Practices whose support conversation this page has already provisioned: once each, not on every reconnect. */
  private readonly supportProvisioned = new Set<string>();

  /**
   * Makes sure the user's conversation with MedicAI Support exists in every practice they belong to, so it sits in the
   * chat list from the start. `conv:openSupport` is find-or-create on the hub, and an empty one reaches no agent: the
   * support dashboard lists a conversation only once a message has been posted in it. Best effort and silent.
   */
  private async provisionSupport(orgs: readonly HubOrg[]): Promise<void> {
    for (const org of orgs) {
      if (this.supportProvisioned.has(org.orgId)) continue;
      this.supportProvisioned.add(org.orgId);
      try {
        await this.store.openSupport(org.orgId);
      } catch {
        this.supportProvisioned.delete(org.orgId); // try again on the next connect
      }
    }
  }

  /**
   * Opens the floating window on the caller's conversation with MedicAI Support (M7): in-app support, beside WhatsApp.
   * One per user per practice (the selected one by default); every support agent sees it on the support dashboard.
   */
  async openSupportChat(groupId?: string | null): Promise<void> {
    await this.whenReady();
    const gid = groupId ?? (await firstValueFrom(this.groupService.selectedGroup$))?.id ?? null;
    const org = this.store.orgByRef(gid ? String(gid) : null);
    if (!org) throw new HubRequestError('NOT_FOUND');
    const c = await this.store.openSupport(org.orgId);
    this.ui.openConversation(c.id);
  }

  /**
   * The patient's chat AS FILED ON THE RECORD, per day (accepted entries only), from this platform's own copy (M2b), so
   * the timeline does not depend on the hub being up. Empty when chat is off or nothing is filed.
   */
  async recordedChatDays(patientId: string): Promise<PatientChatActivity> {
    const token = localStorage.getItem('token');
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: PatientChatActivity['days'] }>(`api/hub/patients/${encodeURIComponent(patientId)}/chat-days`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { tz: String(-new Date().getTimezoneOffset()) },
        }),
      );
      return { conversationId: null, days: res.data ?? [] };
    } catch {
      return { conversationId: null, days: [] };
    }
  }

  /** The patient chat's activity per day, live from the hub. Empty when chat is off or the patient has no chat yet. */
  async patientActivity(patient: { id: string; groupId?: string | null }): Promise<PatientChatActivity> {
    try {
      await this.whenReady();
      return await this.store.patientActivity(this.orgOfPatient(patient), String(patient.id));
    } catch {
      return { conversationId: null, days: [] };
    }
  }

  // ── smart mentions (M3) ───────────────────────────────────────────────────────

  /**
   * The patient index, recent patients and colleagues' surnames of the practice this conversation belongs to. The
   * colleagues come from the hub directory (cached 5 minutes): "Dr <their surname>" is a colleague, not a patient.
   */
  async mentionContext(orgId: string): Promise<MentionContext> {
    let colleagues = this.colleagues.get(orgId);
    if (!colleagues || Date.now() - colleagues.at > 5 * 60_000) {
      try {
        const people = await this.store.directory(orgId);
        colleagues = { at: Date.now(), surnames: new Set(people.map((p) => normalise(p.name).split(' ').pop() ?? '').filter((x) => x.length > 1)) };
        this.colleagues.set(orgId, colleagues);
      } catch {
        colleagues = { at: 0, surnames: new Set() };
      }
    }
    return { index: this.patientIndex, recent: this.recentPatients, colleagueSurnames: colleagues.surnames };
  }

  /** The same context without waiting (the colleague list as last fetched). */
  mentionContextNow(orgId: string): MentionContext {
    return { index: this.patientIndex, recent: this.recentPatients, colleagueSurnames: this.colleagues.get(orgId)?.surnames ?? new Set() };
  }

  /**
   * A task card's Done: completes the task through the same endpoint RelayMD uses. The card changes when the hub
   * sends the updated message (msg:updated).
   */
  async markTaskDone(taskId: string): Promise<void> {
    const token = localStorage.getItem('token');
    await firstValueFrom(this.http.post(`api/relaymd/tasks/${encodeURIComponent(taskId)}/done`, {}, { headers: { Authorization: `Bearer ${token}` } }));
  }

  /**
   * A booking card's Confirm or Cancel, through the same endpoint RelayMD uses (the calendar's own save). The card
   * changes when the hub sends the updated message.
   */
  async setAppointmentStatus(appointmentId: string, status: 'confirmed' | 'cancelled'): Promise<void> {
    const token = localStorage.getItem('token');
    await firstValueFrom(this.http.post(`api/relaymd/appointments/${encodeURIComponent(appointmentId)}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } }));
  }

  /** Links a message into a patient's chat (a frozen copy, §8.4). The title is the patient's name as the chat shows it. */
  linkMessage(messageId: string, patient: PatientCandidate): Promise<MessageView> {
    return this.store.linkPatient(messageId, { patientRef: patient.id, title: patient.title });
  }

  /** `#` mentions here already name clinical patients (the hub's default subject); MedicAI Connect resolves its own. */
  patientSubjects(mentions: readonly PatientMention[]): Promise<PatientMention[]> {
    return Promise.resolve([...mentions]);
  }

  openConversation(conversationId: string): void {
    this.ui.openConversation(conversationId);
  }

  /**
   * Opens the floating window on one conversation BY ID, optionally AT one message.
   *
   * Unlike `openConversation`, this waits for chat to be up and fetches the conversation if this session has not seen
   * it yet: the Inbox's chat notice (§7.1) names a referral chat that may never have been opened here, and "go to the
   * original" on a linked copy (§8.4) names whatever conversation it was written in.
   *
   * It REJECTS when the caller may not read that conversation — following a link back into a channel or a direct
   * message they are not part of — so the caller can say so rather than opening an empty window.
   */
  async openConversationById(conversationId: string, seq?: number): Promise<void> {
    await this.whenReady();
    const id = String(conversationId).toLowerCase();
    if (!this.store.snapshot.conversations[id]) await this.store.refreshConversation(id);
    this.ui.openConversation(id);
    if (seq != null) await this.store.openAt(id, seq);
  }

  conversations$(): Observable<ConversationSummary[]> {
    return combineLatest([this.store.conversationsOf$(this.orgId$), this.groupService.userGroups$]).pipe(
      map(([list, groups]) => {
        const units = stateUnitGroupIds(groups);
        return units.size ? list.filter((c) => visibleInStateMode(c, units, (orgId) => this.groupIdOfOrg(orgId))) : list;
      }),
    );
  }

  /**
   * Unread messages from MedicAI Support, for the badge on Help (2026-09-20). Across every practice: a support chat
   * belongs to the person, not to the practice they happen to be looking at.
   */
  readonly supportUnread$: Observable<number> = this.store.state$.pipe(
    map((s) => Object.values(s.conversations).filter((c) => c.type === 'support').reduce((n, c) => n + c.unread, 0)),
    distinctUntilChanged(),
  );
}
